import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  Table,
  Stack,
  Box,
  Avatar,
  Loader,
  Center,
  Select,
  ActionIcon,
  SimpleGrid,
  Badge,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { Plus, Search, Home, Grid, List, Settings, Download } from 'react-feather';
import { partnersApi } from '../../services/api/partners';
import type { Partner } from '../../types';
import { SortableTableHeader, Pagination, ColumnSettingsPopover, ExportModal, EmptyState } from '../../components/common';
import { AddPartnerModal } from '../../components/partners/AddPartnerModal';
import { useColumnConfig, useFetchList, type ColumnConfig } from '../../hooks';
import classes from './PartnersPage.module.css';

type ViewMode = 'grid' | 'list';
type StatusFilter = 'all' | 'active' | 'inactive';

// Status filter pill options
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Sort options for dropdown
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'code', label: 'Code' },
  { value: 'programs', label: 'Programs' },
];

// Default column configuration for partners table
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'code', label: 'Code', visible: true, sortable: true },
  { id: 'name', label: 'Partner Name', visible: true, sortable: true },
  { id: 'programs', label: 'Programs', visible: true, sortable: false },
];

const SORT_BY_MAP: Record<string, string> = {
  code: 'code',
  name: 'name',
  programs: 'program_count',
};

export function PartnersPage() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);

  // Use the reusable column config hook
  const {
    columns,
    setColumns,
    sortConfig,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    visibleColumnCount,
  } = useColumnConfig({
    preferenceKey: 'partners_list_settings',
    defaultColumns: DEFAULT_COLUMNS,
    defaultPageSize: 25,
  });

  const fetchPartners = useCallback(() => {
    const effectiveSortBy = sortConfig?.key || sortBy;
    const apiSortBy = SORT_BY_MAP[effectiveSortBy] || 'name';
    const apiSortOrder = sortConfig?.order || 'ASC';
    const isActiveFilter = selectedStatusFilter === 'all'
      ? undefined
      : selectedStatusFilter === 'active';

    return partnersApi.list({
      search: debouncedSearch || undefined,
      sortBy: apiSortBy,
      sortOrder: apiSortOrder,
      page,
      limit: pageSize,
      isActive: isActiveFilter,
    });
  }, [debouncedSearch, selectedStatusFilter, sortBy, sortConfig, page, pageSize]);

  const { data: partners, loading, totalCount, refetch } = useFetchList(fetchPartners);
  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePartnerCreated = (_partner: Partner) => {
    setCreateModalOpen(false);
    refetch();
  };

  const formatProgramCount = (count: number) => {
    if (count === 0) return '0 programs';
    if (count === 1) return '1 program';
    return `${count} programs`;
  };

  // Render column header based on column config
  const renderColumnHeader = (column: ColumnConfig) => {
    if (column.sortable) {
      return (
        <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
          <SortableTableHeader
            label={column.label.toUpperCase()}
            sortKey={column.id}
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </Table.Th>
      );
    }
    return (
      <Table.Th key={column.id} className={classes[`${column.id}Column`]}>
        {column.label.toUpperCase()}
      </Table.Th>
    );
  };

  // Render cell content based on column id
  const renderCellContent = (column: ColumnConfig, partner: Partner) => {
    switch (column.id) {
      case 'code':
        return (
          <Text fw={600} size="sm" c="dimmed">
            {partner.code}
          </Text>
        );
      case 'name':
        return (
          <Group gap="sm">
            <Avatar size="sm" radius="xl" color="medleyGreen">
              {partner.code?.substring(0, 2) || partner.name.substring(0, 2).toUpperCase()}
            </Avatar>
            <Text fw={500}>{partner.name}</Text>
          </Group>
        );
      case 'programs':
        return <Text size="sm">{formatProgramCount(partner.program_count)}</Text>;
      default:
        return <Text size="sm">—</Text>;
    }
  };

  // Empty state (only show if no filters applied)
  const hasFilters = debouncedSearch || selectedStatusFilter !== 'all';
  if (!loading && partners.length === 0 && !hasFilters) {
    return (
      <Container size="xl" py="xl">
        <Title order={1} mb="xl">
          Partners
        </Title>
        <EmptyState
          icon={<Home size={40} />}
          title="No partners yet"
          description="Get started by creating your first partner organization."
          action={
            <Button leftSection={<Plus size={18} />} onClick={() => setCreateModalOpen(true)}>
              Create Partner
            </Button>
          }
          size="lg"
          withCard
        />

        <AddPartnerModal
          opened={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handlePartnerCreated}
        />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={1}>Partners</Title>
        <Group gap="sm">
          <Button variant="default" leftSection={<Download size={18} />} onClick={openExportModal}>
            Export
          </Button>
          <Button leftSection={<Plus size={18} />} onClick={() => setCreateModalOpen(true)}>
            Create Partner
          </Button>
        </Group>
      </Group>

      {/* Search and Filter Panel */}
      <Card withBorder p="md" mb="lg" className={classes.filterPanel} styles={{ root: { boxShadow: 'none' } }}>
        <Group justify="space-between" wrap="nowrap">
          {/* Left: Search and Filter Pills */}
          <Group gap="md" wrap="nowrap">
            <TextInput
              placeholder="Search partners..."
              leftSection={<Search size={16} />}
              value={searchValue}
              onChange={e => setSearchValue(e.currentTarget.value)}
              className={classes.searchInput}
            />

            {/* Status Filter Pills */}
            <Group gap={4}>
              {STATUS_FILTER_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  variant={selectedStatusFilter === option.value ? 'filled' : 'outline'}
                  color={selectedStatusFilter === option.value ? 'medleyGreen' : 'gray'}
                  size="xs"
                  className={classes.filterPill}
                  onClick={() => {
                    setSelectedStatusFilter(option.value as StatusFilter);
                    setPage(1); // Reset to first page when filter changes
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </Group>
          </Group>

          {/* Right: Sort and View Toggle */}
          <Group gap="md" wrap="nowrap">
            <Select
              placeholder="Sort by"
              data={SORT_OPTIONS}
              value={sortBy}
              onChange={value => setSortBy(value || 'name')}
              leftSection={<Text size="xs" c="dimmed">Sort by:</Text>}
              leftSectionWidth={60}
              w={180}
              size="sm"
              styles={{
                input: { paddingLeft: 60 },
              }}
            />

            {/* View Mode Toggle */}
            <Group gap={0} className={classes.viewToggle}>
              <ActionIcon
                variant={viewMode === 'grid' ? 'filled' : 'subtle'}
                color={viewMode === 'grid' ? 'medleyGreen' : 'gray'}
                size="md"
                onClick={() => setViewMode('grid')}
                className={classes.viewToggleButton}
              >
                <Grid size={16} />
              </ActionIcon>
              <ActionIcon
                variant={viewMode === 'list' ? 'filled' : 'subtle'}
                color={viewMode === 'list' ? 'medleyGreen' : 'gray'}
                size="md"
                onClick={() => setViewMode('list')}
                className={classes.viewToggleButton}
              >
                <List size={16} />
              </ActionIcon>
            </Group>
          </Group>
        </Group>
      </Card>

      {/* Content */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : viewMode === 'list' ? (
        /* Table/List View */
        <Card withBorder p={0} className={classes.tableContainer} styles={{ root: { boxShadow: 'none' } }}>
          <Table.ScrollContainer minWidth={600}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  {columns
                    .filter(col => col.visible)
                    .map(col => renderColumnHeader(col))}
                  {/* Settings Gear Column */}
                  <Table.Th className={classes.settingsColumn}>
                    <ColumnSettingsPopover
                      columns={columns}
                      onColumnsChange={setColumns}
                      trigger={
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <Settings size={16} />
                        </ActionIcon>
                      }
                    />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {partners.map(partner => (
                  <Table.Tr
                    key={partner.id}
                    className={classes.tableRow}
                    onClick={() => navigate(`/partners/${partner.id}`)}
                  >
                    {columns
                      .filter(col => col.visible)
                      .map(col => (
                        <Table.Td key={col.id}>
                          {renderCellContent(col, partner)}
                        </Table.Td>
                      ))}
                    {/* Empty cell for settings column */}
                    <Table.Td />
                  </Table.Tr>
                ))}
                {partners.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={visibleColumnCount + 1}>
                      <Text ta="center" c="dimmed" py="xl">
                        No partners found matching your filters
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalCount > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </Card>
      ) : (
        /* Grid View */
        <>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {partners.map(partner => (
              <Card
                key={partner.id}
                withBorder
                padding="lg"
                className={classes.gridCard}
                onClick={() => navigate(`/partners/${partner.id}`)}
              >
                <Stack align="center" gap="sm">
                  <Avatar
                    size={80}
                    radius={80}
                    color="medleyGreen"
                    variant="filled"
                  >
                    {partner.code?.substring(0, 2) || partner.name.substring(0, 2).toUpperCase()}
                  </Avatar>
                  <Text fw={600} size="lg" ta="center" lineClamp={1}>
                    {partner.name}
                  </Text>
                  {partner.code && (
                    <Text size="sm" c="dimmed">
                      {partner.code}
                    </Text>
                  )}
                  <Badge
                    color={partner.is_active ? 'medleyGreen' : 'gray'}
                    variant={partner.is_active ? 'filled' : 'light'}
                    size="sm"
                    tt="uppercase"
                  >
                    {partner.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Stack>

                {/* Stats Footer */}
                <Box className={classes.statsFooter}>
                  <Group justify="center" gap="xl">
                    <Box ta="center">
                      <Text fw={600} size="lg">
                        {partner.program_count}
                      </Text>
                      <Text size="xs" c="dimmed" tt="uppercase">
                        Programs
                      </Text>
                    </Box>
                    <Box ta="center">
                      <Text fw={600} size="lg">
                        {partner.cohort_count || 0}
                      </Text>
                      <Text size="xs" c="dimmed" tt="uppercase">
                        Cohorts
                      </Text>
                    </Box>
                  </Group>
                </Box>
              </Card>
            ))}
          </SimpleGrid>

          {partners.length === 0 && (
            <Card withBorder p="xl">
              <Text ta="center" c="dimmed">
                No partners found matching your filters
              </Text>
            </Card>
          )}

          {totalCount > 0 && (
            <Card withBorder p={0} mt="md">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={totalCount}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </Card>
          )}
        </>
      )}

      <AddPartnerModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handlePartnerCreated}
      />

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="partners"
        recordCount={totalCount}
        filterContext={{
          search: debouncedSearch || undefined,
          isActive: selectedStatusFilter === 'all' ? undefined : selectedStatusFilter === 'active',
        }}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        filterSummary={debouncedSearch ? [{ label: 'Search', value: debouncedSearch }] : undefined}
        // The export worker applies only the search to partners (MDF-208).
        ignoredFilters={selectedStatusFilter !== 'all' ? ['Status'] : undefined}
      />
    </Container>
  );
}

export default PartnersPage;
